<script context="module">
  let count = 0;
  export const getId = () => count++;
</script>

<script lang="ts">
  import MaterialIcon from '$lib/MaterialIcon.svelte';
  import { mdiCheck } from '@mdi/js';
  import { omit } from 'shared/src/utils/omit';
  import { twMerge } from 'tailwind-merge';

  interface $$Props extends Partial<HTMLInputElement> {
    checked?: boolean;
    class?: string;
    /**
     * Applied to the check box & material icon.
     */
    iconClass?: string;
    /**
     * Applied to the label
     */
    labelClass?: string;
    color?: CheckboxColor;
    /**
     * Defaults false
     */
    rounded?: true | false;
  }

  export let checked: boolean = false;
  export let id = `utheory-checkbox-${getId()}`;
  let clazz = '';
  export { clazz as class };
  export let iconClass = '';
  export let labelClass = '';
  export let rounded: boolean = false;

  type CheckboxColor = 'red' | 'orange' | 'green' | 'blue' | 'cyan' | 'gray' | 'white';
  export let color: CheckboxColor = 'cyan';
  let selectedColor: string;
  let selectedBorderColor: string;
  let unselectedBorderColor = 'border-gray outline-gray';

  $: {
    switch (color) {
      case 'orange':
        selectedColor = 'text-orange-500 high-contrast:text-orange-800';
        selectedBorderColor =
          'border-orange-500 high-contrast:border-orange-800 outline-orange-500 high-contrast:outline-orange-800';
        unselectedBorderColor = 'border-gray outline-gray';

        break;
      case 'green':
        selectedColor = 'text-green-500 high-contrast:text-green-700';
        selectedBorderColor =
          'border-green-500 high-contrast:border-green-700 outline-green-500 high-contrast:outline-green-700';
        unselectedBorderColor = 'border-gray outline-gray';

        break;
      case 'red':
        selectedColor = 'text-red-500 high-contrast:text-red-600';
        selectedBorderColor =
          'border-red-500 high-contrast:border-red-600 outline-red-500 high-contrast:outline-red-600';

        unselectedBorderColor = 'border-gray outline-gray';

        break;
      case 'blue':
        selectedColor = 'text-blue-500 high-contrast:text-blue-700';
        selectedBorderColor =
          'border-blue-500 high-contrast:border-blue-700 outline-blue-500 high-contrast:outline-blue-700';
        unselectedBorderColor = 'border-gray outline-gray';

        break;
      case 'gray':
        selectedColor = 'text-gray-400 high-contrast:text-gray-500';
        selectedBorderColor =
          'border-gray-400 high-contrast:border-gray-500 outline-gray-400 high-contrast:outline-gray-500';
        unselectedBorderColor = 'border-gray outline-gray';
        break;
      case 'white':
        selectedColor = 'text-white high-contrast:text-white';
        selectedBorderColor =
          'border-white high-contrast:border-white outline-white high-contrast:outline-white';
        unselectedBorderColor = 'border-white outline-white';
        break;
      case 'cyan':
      default:
        selectedColor = 'text-cyan-500 high-contrast:text-cyan-700';
        selectedBorderColor =
          'border-cyan-500 high-contrast:border-cyan-700 outline-cyan-500 high-contrast:outline-cyan-700';
        unselectedBorderColor = 'border-gray outline-gray';
        break;
    }
  }
  let checkInput: HTMLInputElement;
</script>

<div class={twMerge('flex', clazz)}>
  <div class="utheory-checkbox-div">
    <span>
      <input
        bind:this={checkInput}
        {id}
        type="checkbox"
        bind:checked
        {...omit($$props, 'checked')}
        on:change
        on:abort
        on:blur
        on:click
        on:focus
        on:input
        on:invalid
        on:keydown
        on:keypress
        on:keyup
        on:mousedown
        on:mouseenter
        on:mouseleave
        on:mousemove
        on:mouseout
        on:mouseover
        on:mouseup
      />
      <div
        class="checkbox-icon {checked ? selectedColor : 'text-transparent'} "
        on:click={() => {
          // So that we trigger on:click
          checkInput.click();
          checkInput.focus();
        }}
      >
        <MaterialIcon
          class={twMerge(
            `check-icon border ${rounded ? 'rounded-md' : ''} transition-all ${
              checked ? selectedBorderColor : unselectedBorderColor
            }`,
            iconClass
          )}
          size="0.88em"
          hideLabel
          path={mdiCheck}
        />
      </div>
    </span>
  </div>
  {#if Object.keys($$slots).length}
    <label class={twMerge('ml-2', labelClass)} for={id}><slot /></label>
  {/if}
</div>

<style lang="postcss">
  input[type='checkbox'] {
    @apply opacity-0 w-0 h-0 top-0 left-0 overflow-hidden absolute;
  }

  .checkbox-icon {
    @apply absolute my-auto;
    /**
     * There is no magic to this number, it's just trial-and-error to look
     * centered on the text with ascenders & descenders
     */
    top: -0.15em;
    left: 0;
  }

  .utheory-checkbox-div {
    width: 1em;
    height: 1em;
    min-width: 1em;
    min-height: 1em;
    @apply relative;
    &:focus-within {
      :global(.check-icon) {
        @apply outline-2 outline-double;
      }
    }
  }
</style>
